import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  api,
  ApiError,
  NotAuthenticatedError,
  toFigureLite,
  type FigureLite,
  type Thread,
  type UserSettingsResponse,
} from '../api'
import { getApiBase, resolveMediaUrl } from '../utils/media'
import {
  buildThreadCreatePayload,
  ensureValidFigureSlug,
  loadFigureSlugRegistry,
  OUT_OF_DATE_FIGURE_MESSAGE,
  primeFigureSlugRegistry,
} from '../utils/figureSlugRegistry'
import { getKnowledgeScopeLabel, getLanguageModeLabel, getToneLevelLabel } from '../constants/chatPreferences'
import { useAuth } from '../auth/AuthProvider'
import FigureThumb from '../components/FigureThumb'
import SourceDepthBadge from '../components/SourceDepthBadge'

const normalizeImageSrc = (value?: string | null) => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

const buildInitials = (value?: string | null) => {
  if (!value) return 'PiT'
  const parts = value.trim().split(/\s+/)
  const initials = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('')
  return initials || 'PiT'
}

type SafeFigureThumbProps = {
  rawSrc?: string | null
  alt?: string
  size: number
  variant?: 'circle' | 'rounded'
  fallbackText?: string
  cacheKey: string
}

const SafeFigureThumb = ({ rawSrc, alt = 'Figure portrait', size, variant = 'circle', fallbackText, cacheKey }: SafeFigureThumbProps) => {
  const [src, setSrc] = useState<string | null>(normalizeImageSrc(rawSrc))
  const [retried, setRetried] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    setSrc(normalizeImageSrc(rawSrc))
    setRetried(false)
    setLoaded(false)
  }, [rawSrc])

  useEffect(() => {
    const img = imgRef.current
    if (!img) return
    if (!loaded && img.complete && img.naturalWidth > 0) {
      setLoaded(true)
    }
  }, [src, loaded])

  const initials = useMemo(() => buildInitials(fallbackText || alt), [fallbackText, alt])

  const handleError = () => {
    if (!src) return
    if (!retried) {
      const cacheBusted = `${src}${src.includes('?') ? '&' : '?'}v=${Date.now()}`
      setSrc(cacheBusted)
      setRetried(true)
      setLoaded(false)
      return
    }
    setSrc(null)
  }

  const classes = ['figure-thumb', variant].filter(Boolean).join(' ')

  if (!src) {
    return (
      <span className={classes} style={{ width: size, height: size }}>
        <span className="figure-thumb-monogram">{initials}</span>
      </span>
    )
  }

  return (
    <span className={classes} style={{ width: size, height: size }}>
      <img
        key={`${cacheKey}:${src}`}
        className={`figure-thumb-image${loaded ? ' is-loaded' : ''}`}
        src={src}
        alt={alt}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        style={{ objectFit: 'cover', width: size, height: size }}
        ref={imgRef}
        onError={handleError}
        onLoad={() => setLoaded(true)}
      />
    </span>
  )
}

type SettingsValues = {
  language_mode: UserSettingsResponse['language_mode']
  knowledge_scope: UserSettingsResponse['knowledge_scope']
  tone_level: UserSettingsResponse['tone_level']
}

const FALLBACK_SETTINGS: SettingsValues = {
  language_mode: 'modern',
  knowledge_scope: 'bounded',
  tone_level: 'adult',
}

const API_BASE = getApiBase()

const FIGURE_CACHE_KEY = 'pit_dashboard_figures'

const mergeSettings = (response: UserSettingsResponse | null): { values: SettingsValues; defaults: SettingsValues } => {
  const defaults = {
    ...FALLBACK_SETTINGS,
    ...(response?.defaults ?? {}),
  }

  return {
    defaults,
    values: {
      language_mode: response?.language_mode ?? defaults.language_mode,
      knowledge_scope: response?.knowledge_scope ?? defaults.knowledge_scope,
      tone_level: response?.tone_level ?? defaults.tone_level,
    },
  }
}

const loadFigureCache = (): Record<string, FigureLite> => {
  try {
    const raw = window.sessionStorage.getItem(FIGURE_CACHE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        let dirty = false
        const cleaned = Object.entries(parsed).reduce<Record<string, FigureLite>>((acc, [slug, figure]) => {
          const hasForbidden = Boolean(figure && typeof figure === 'object' && ('contexts' in (figure as any) || 'figure_contexts' in (figure as any)))
          if (hasForbidden) {
            dirty = true
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.warn('[DASHBOARD] Dropping cached figure with forbidden context keys', { slug })
            }
            return acc
          }
          acc[slug] = toFigureLite(figure as any)
          return acc
        }, {})
        if (dirty) {
          persistFigureCache(cleaned)
        }
        return cleaned
      }
    }
  } catch {
    // ignore
  }
  return {}
}

const persistFigureCache = (cache: Record<string, FigureLite>) => {
  try {
    window.sessionStorage.setItem(FIGURE_CACHE_KEY, JSON.stringify(cache))
  } catch {
    // ignore
  }
}

const describeFigure = (figure?: FigureLite): string | null => {
  if (!figure) return null
  if (figure.short_summary) return figure.short_summary
  if (figure.era_label) return figure.era_label
  if (figure.pronunciation) return figure.pronunciation
  return null
}

const resolveFigureImage = (figure?: FigureLite | null) => {
  if (!figure) return undefined
  const candidate = figure.image_url ?? null
  const resolved = resolveMediaUrl(API_BASE, candidate)
  return normalizeImageSrc(resolved) ?? undefined
}

const pickThreadTimestamp = (thread: Thread): string | null => {
  const lastMessageAt = (thread as Thread & { last_message_at?: string }).last_message_at
  return lastMessageAt || thread.updated_at || thread.created_at || null
}

const formatShortDateTime = (isoString?: string | null): string | null => {
  if (!isoString) return null
  try {
    const formatter = new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
    return formatter.format(new Date(isoString))
  } catch {
    return null
  }
}

const Dashboard = () => {
  const navigate = useNavigate()
  const { token, ready, logout } = useAuth()

  const [threads, setThreads] = useState<Thread[]>([])
  const [threadsLoading, setThreadsLoading] = useState(false)
  const [threadsError, setThreadsError] = useState<string | null>(null)

  const [settings, setSettings] = useState<SettingsValues>(FALLBACK_SETTINGS)
  const [settingsDefaults, setSettingsDefaults] = useState<SettingsValues>(FALLBACK_SETTINGS)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [settingsLoading, setSettingsLoading] = useState(false)

  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<FigureLite[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [searchTimer, setSearchTimer] = useState<number | null>(null)
  const [typeaheadOpen, setTypeaheadOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState<number>(-1)

  const [creatingThreadSlug, setCreatingThreadSlug] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const [lastCreateSlug, setLastCreateSlug] = useState<string | null>(null)

  const [figureCache, setFigureCache] = useState<Record<string, FigureLite>>(loadFigureCache)
  const figureCacheRef = useRef<Record<string, FigureLite>>(figureCache)
  const typeaheadRef = useRef<HTMLDivElement | null>(null)
  const typeaheadInputRef = useRef<HTMLInputElement | null>(null)

  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renamingTitle, setRenamingTitle] = useState<string>('')
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const canRun = ready && Boolean(token)

  useEffect(() => {
    if (!canRun) return
    let cancelled = false
    loadFigureSlugRegistry().catch(() => {
      if (!cancelled && import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[DASHBOARD] Could not prefetch figure slug registry')
      }
    })
    return () => {
      cancelled = true
    }
  }, [canRun])

  useEffect(() => {
    if (!ready) return
    if (!token) {
      navigate('/login?next=/dashboard', { replace: true })
    }
  }, [ready, token, navigate])

  const fetchThreads = useCallback(() => {
    if (!canRun) return
    let cancelled = false
    setThreadsLoading(true)
    setThreadsError(null)

    api
      .listThreads(token ?? undefined)
      .then((data) => {
        if (!cancelled) {
          const list = Array.isArray(data) ? data : []
          const sorted = [...list].sort((a, b) => {
            const aTime = new Date(a.updated_at ?? a.created_at ?? 0).getTime()
            const bTime = new Date(b.updated_at ?? b.created_at ?? 0).getTime()
            return bTime - aTime
          })
          setThreads(sorted)
        }
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof NotAuthenticatedError || (err instanceof ApiError && err.status === 401)) {
          logout()
          navigate('/login?next=/dashboard', { replace: true })
          return
        }
        const baseMessage = err instanceof ApiError ? err.message : 'Unable to load threads right now.'
        const statusText = err instanceof ApiError && err.status ? ` (status ${err.status})` : ''
        setThreadsError(`${baseMessage}${statusText}`)
      })
      .finally(() => {
        if (!cancelled) {
          setThreadsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [api, canRun, token, logout, navigate])

  useEffect(() => {
    const cancel = fetchThreads()
    return cancel
  }, [fetchThreads])

  const fetchSettings = useCallback(() => {
    if (!canRun) return
    let cancelled = false
    setSettingsLoading(true)
    setSettingsError(null)

    api
      .getUserSettings(token ?? undefined)
      .then((data) => {
        if (cancelled) return
        const { values, defaults } = mergeSettings(data)
        setSettings(values)
        setSettingsDefaults(defaults)
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof NotAuthenticatedError || (err instanceof ApiError && err.status === 401)) {
          logout()
          navigate('/login?next=/dashboard', { replace: true })
          return
        }
        const message = err instanceof ApiError ? err.message : 'Unable to load your settings summary.'
        setSettingsError(message)
      })
      .finally(() => {
        if (!cancelled) {
          setSettingsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [api, canRun, token, logout, navigate])

  useEffect(() => {
    const cancel = fetchSettings()
    return cancel
  }, [fetchSettings])

  useEffect(() => {
    return () => {
      if (searchTimer) {
        window.clearTimeout(searchTimer)
      }
    }
  }, [searchTimer])

  useEffect(() => {
    figureCacheRef.current = figureCache
    persistFigureCache(figureCache)
    primeFigureSlugRegistry(Object.values(figureCache))
  }, [figureCache])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeaheadRef.current && event.target instanceof Node && !typeaheadRef.current.contains(event.target)) {
        setTypeaheadOpen(false)
        setHighlightIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSearch = async (event?: FormEvent) => {
    if (event) event.preventDefault()
    const term = searchTerm.trim()
    if (!term || !token) {
      setSearchResults([])
      return
    }

    setSearchLoading(true)
    setSearchError(null)

    try {
      const results = await api.searchFigures(term, 8)
      const lites = results.map((fig) => toFigureLite(fig))
      primeFigureSlugRegistry(lites)
      setSearchResults(lites)
    } catch (err) {
      const message = err instanceof ApiError ? `${err.message}` : 'Search failed. Please try again.'
      setSearchError(message)
      setSearchResults([])
      // Log server validation details for debugging 422s without exposing to users.
      if (err instanceof ApiError && err.details) {
        // eslint-disable-next-line no-console
        console.debug('figure search error', err.details)
      }
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setTypeaheadOpen(true)
    setHighlightIndex(-1)
    if (searchTimer) {
      window.clearTimeout(searchTimer)
    }
    if (value.trim().length < 2) {
      setSearchResults([])
      return
    }
    const timer = window.setTimeout(() => {
      void handleSearch()
    }, 300)
    setSearchTimer(timer)
  }

  const handleStartChat = async (figureSlug: string) => {
    if (!token || !figureSlug) return

    setCreatingThreadSlug(figureSlug)
    setLastCreateSlug(figureSlug)
    setCreateError(null)
    setTypeaheadOpen(false)

    try {
      const payload = buildThreadCreatePayload(figureSlug)
      const isValidSlug = await ensureValidFigureSlug(payload.figure_slug)
      if (!isValidSlug) {
        setCreateError(OUT_OF_DATE_FIGURE_MESSAGE)
        return
      }

      const thread = await api.createThread(payload, token)
      if (thread?.id) {
        navigate(`/chat/${thread.id}`)
      } else {
        setCreateError('Could not start chat. No thread id returned.')
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Unable to create a new chat right now.'
      setCreateError(message)
    } finally {
      setCreatingThreadSlug(null)
    }
  }

  const handleDeleteThread = async (threadId: string) => {
    if (!token) return
    setDeleteError(null)
    try {
      await api.deleteThread(threadId, token)
      setThreads((prev) => prev.filter((t) => t.id !== threadId))
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Unable to delete this thread.'
      setDeleteError(message)
    }
  }

  const handleRenameThread = async (threadId: string) => {
    if (!token || !renamingTitle.trim()) return
    setDeleteError(null)
    setRenamingId(threadId)
    try {
      const updated = await api.renameThread(threadId, renamingTitle.trim(), token)
      setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, title: updated.title } : t)))
      setRenamingTitle('')
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Unable to rename this thread.'
      setDeleteError(message)
    } finally {
      setRenamingId(null)
    }
  }

  const getHeroThread = useCallback((list: Thread[]): Thread | null => {
    if (!list.length) return null
    const sorted = [...list].sort((a, b) => {
      const aTime = new Date(a.updated_at ?? a.created_at ?? 0).getTime()
      const bTime = new Date(b.updated_at ?? b.created_at ?? 0).getTime()
      return bTime - aTime
    })
    return sorted[0] ?? null
  }, [])

  const heroThread = useMemo(() => getHeroThread(threads), [threads, getHeroThread])

  const figureForSlug = useCallback(
    (slug?: string | null): FigureLite | undefined => {
      if (!slug) return undefined
      return figureCacheRef.current[slug]
    },
    [],
  )

  const hydrateFigures = useCallback(
    async (threadList: Thread[]) => {
      const slugs = Array.from(new Set(threadList.map((t) => t.figure_slug).filter(Boolean))) as string[]
      const missing = slugs.filter((slug) => !figureCacheRef.current[slug])
      if (!missing.length) return

      try {
        const results = await Promise.all(
          missing.map(async (slug) => {
            try {
              const fig = await api.getFigureLite(slug)
              return { slug, fig }
            } catch {
              return null
            }
          }),
        )
        const nextCache = { ...figureCacheRef.current }
        results.forEach((item) => {
          if (item?.slug && item.fig) {
            nextCache[item.slug] = item.fig
          }
        })
        primeFigureSlugRegistry(Object.values(nextCache))
        setFigureCache(nextCache)
      } catch {
        // ignore fetch errors here; threads will still render with fallbacks
      }
    },
    [api],
  )

  useEffect(() => {
    if (threads.length) {
      void hydrateFigures(threads)
    }
  }, [threads, hydrateFigures])

  const effectiveSettings = useMemo(() => {
    return {
      language_mode: settings.language_mode ?? settingsDefaults.language_mode,
      knowledge_scope: settings.knowledge_scope ?? settingsDefaults.knowledge_scope,
      tone_level: settings.tone_level ?? settingsDefaults.tone_level,
    }
  }, [settings, settingsDefaults])

  const formatLastActive = (thread: Thread): string | null => {
    const date = thread.updated_at || thread.created_at
    if (!date) return null
    const delta = Date.now() - new Date(date).getTime()
    const minutes = Math.max(0, Math.round(delta / (1000 * 60)))
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.round(hours / 24)
    return `${days}d ago`
  }

  const heroMetaLine = (thread: Thread): string => {
    const parts: string[] = []
    const lastActive = formatLastActive(thread)
    if (lastActive) parts.push(`Last active ${lastActive}`)
    parts.push(`Tone: ${getToneLevelLabel(effectiveSettings.tone_level)}`)
    parts.push(`Knowledge: ${getKnowledgeScopeLabel(effectiveSettings.knowledge_scope)}`)
    return parts.join(' · ')
  }

  const friendlyThreadTitle = useCallback(
    (thread: Thread): string => {
      const rawTitle = thread.title?.trim()
      if (rawTitle) return rawTitle
      const figure = figureForSlug(thread.figure_slug)
      const name = figure?.name
      if (name) return `Chat with ${name}`
      if (thread.figure_slug) return `Chat with ${thread.figure_slug}`
      return 'Chat'
    },
    [figureForSlug],
  )

  const renderThreadSnippet = (thread: Thread) => {
    const figure = figureForSlug(thread.figure_slug)
    if (thread.last_message) return thread.last_message
    const descriptor = describeFigure(figure)
    if (descriptor) return descriptor
    return 'Conversation ready to resume.'
  }

  const suggestionFigures = useMemo(() => {
    const ordered = [...threads].sort((a, b) => {
      const aTime = new Date(a.updated_at ?? a.created_at ?? 0).getTime()
      const bTime = new Date(b.updated_at ?? b.created_at ?? 0).getTime()
      return bTime - aTime
    })
    const seen = new Set<string>()
    const picks: FigureLite[] = []
    ordered.forEach((thread) => {
      if (!thread.figure_slug) return
      if (seen.has(thread.figure_slug)) return
      const fig = figureForSlug(thread.figure_slug)
      if (fig) {
        seen.add(thread.figure_slug)
        picks.push(fig)
      }
    })
    return picks.slice(0, 8)
  }, [threads, figureCache, figureForSlug])

  const visibleTypeahead = useMemo(() => {
    if (searchTerm.trim().length >= 2) {
      return searchResults
    }
    return suggestionFigures
  }, [searchResults, searchTerm, suggestionFigures])

  const featuredFigures = useMemo(() => {
    if (suggestionFigures.length) return suggestionFigures.slice(0, 6)
    return Object.values(figureCache).slice(0, 6)
  }, [suggestionFigures, figureCache])

  useEffect(() => {
    setHighlightIndex(-1)
  }, [visibleTypeahead])

  const selectFigure = (figure: FigureLite) => {
    if (!figure?.slug) return
    setTypeaheadOpen(false)
    setSearchTerm('')
    setSearchResults([])
    setHighlightIndex(-1)
    void handleStartChat(figure.slug)
  }

  const focusTypeahead = () => {
    setTypeaheadOpen(true)
    requestAnimationFrame(() => {
      typeaheadInputRef.current?.focus()
    })
  }

  const handleTypeaheadKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setTypeaheadOpen(false)
      setHighlightIndex(-1)
      return
    }

    if (!visibleTypeahead.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setTypeaheadOpen(true)
      setHighlightIndex((prev) => {
        const next = prev + 1
        return next >= visibleTypeahead.length ? 0 : next
      })
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setTypeaheadOpen(true)
      setHighlightIndex((prev) => {
        const next = prev - 1
        return next < 0 ? visibleTypeahead.length - 1 : next
      })
    }

    if (event.key === 'Enter') {
      if (highlightIndex >= 0 && visibleTypeahead[highlightIndex]) {
        event.preventDefault()
        selectFigure(visibleTypeahead[highlightIndex])
      }
    }
  }

  return (
    <section className="dashboard-page">
      <div className="page-header">
        <p className="eyebrow">Dashboard</p>
        <h1>Welcome back</h1>
        <p>Resume your conversations, tune your assistant, and launch new chats from one place.</p>
      </div>

      {!canRun && <p className="error-state">You need to be logged in to view the dashboard.</p>}

      <section className="dashboard-hero">
        {threadsLoading && (
          <div className="hero-body loading">
            <div className="hero-thumb-skeleton" aria-hidden />
            <div className="hero-text">
              <div className="skeleton-line wide" />
              <div className="skeleton-line" />
              <div className="skeleton-line short" />
            </div>
            <div className="hero-actions">
              <div className="skeleton-button" />
              <div className="skeleton-button ghost" />
              <div className="skeleton-button ghost" />
            </div>
          </div>
        )}

        {!threadsLoading && heroThread && (() => {
          const figure = figureForSlug(heroThread.figure_slug)
          const imageSrc = resolveFigureImage(figure)
          const cacheKey = `${heroThread.id}:${imageSrc ?? 'missing'}`
          const fallbackLabel = figure?.name ?? heroThread.figure_slug ?? 'PiT'
          const title = heroThread.title?.trim() || figure?.name || 'Conversation'
          const snippet = heroThread.last_message || 'Pick up where you left off.'
          const heroTimestamp = formatShortDateTime(pickThreadTimestamp(heroThread))

          return (
            <div className="hero-body">
              <div className="hero-left">
                <SafeFigureThumb
                  rawSrc={imageSrc}
                  alt={figure?.name ?? 'Figure portrait'}
                  size={56}
                  variant="circle"
                  fallbackText={fallbackLabel}
                  cacheKey={cacheKey}
                />
                <div className="hero-text">
                  <p className="eyebrow strong">Continue where you left off</p>
                  <h2 className="clamp-1">{title}</h2>
                  {heroTimestamp && <p className="hero-meta clamp-1">{heroTimestamp}</p>}
                  <p className="hero-meta clamp-1">{heroMetaLine(heroThread)}</p>
                  <SourceDepthBadge depth={figure?.source_depth} variant="compact" />
                  <p className="hero-snippet clamp-1">{snippet}</p>
                </div>
              </div>
              <div className="hero-actions">
                <button
                  type="button"
                  className="button primary"
                  onClick={() => navigate(`/chat/${heroThread.id}`)}
                >
                  Resume
                </button>
                <button type="button" className="button ghost" onClick={() => focusTypeahead()}>
                  Start new chat
                </button>
                <button type="button" className="button" onClick={() => navigate('/settings')}>
                  Settings
                </button>
              </div>
            </div>
          )
        })()}

        {!threadsLoading && !heroThread && (
          <div className="hero-body">
            <div className="hero-left">
              <FigureThumb size={56} variant="circle" fallbackText="PiT" />
              <div className="hero-text">
                <p className="eyebrow strong">Welcome</p>
                <h2 className="clamp-1">Start your first conversation</h2>
                <p className="hero-meta clamp-1">Pick any historical figure to begin.</p>
              </div>
            </div>
            <div className="hero-actions">
              <button type="button" className="button primary" onClick={() => focusTypeahead()}>
                Choose a figure
              </button>
              <button type="button" className="button ghost" onClick={() => navigate('/people')}>
                Explore figures
              </button>
              <button type="button" className="button" onClick={() => navigate('/settings')}>
                Settings
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="dashboard-grid">
        <section className="dashboard-card">
          <div className="dashboard-card-heading">
            <h2>Conversations</h2>
            {threadsLoading && <span className="badge">Loading…</span>}
          </div>
          {threadsError && (
            <div className="inline-error-card">
              <div>
                <p className="error-title">Threads unavailable</p>
                <p className="error-state">{threadsError}</p>
              </div>
              <button type="button" className="button" onClick={() => fetchThreads()} disabled={threadsLoading}>
                Retry
              </button>
            </div>
          )}
          {!threadsLoading && threads.length === 0 && !threadsError && (
            <p>No conversations yet. Start one from the panel on the right.</p>
          )}
          <ul className="thread-list">
            {threads.map((thread) => {
              const figure = figureForSlug(thread.figure_slug)
              const imageSrc = resolveFigureImage(figure)
              const cacheKey = `${thread.id}:${imageSrc ?? 'missing'}`
              const fallbackLabel = figure?.name ?? thread.figure_slug ?? 'PiT'
              const shortTimestamp = formatShortDateTime(pickThreadTimestamp(thread))

              return (
                <li key={thread.id} className="thread-list-item">
                  <div className="thread-row">
                      <SafeFigureThumb
                        rawSrc={imageSrc}
                        alt={figure?.name ?? 'Figure portrait'}
                        size={48}
                        variant="circle"
                        fallbackText={fallbackLabel}
                        cacheKey={cacheKey}
                      />
                    <div className="thread-body">
                      <p className="thread-title">{friendlyThreadTitle(thread)}</p>
                      <p className="thread-subtitle clamp-1">{figure?.name ?? 'Figure conversation'}</p>
                      <SourceDepthBadge depth={figure?.source_depth} variant="compact" />
                      {shortTimestamp && <p className="thread-meta clamp-1">{shortTimestamp}</p>}
                      <p className="thread-meta clamp-2">{renderThreadSnippet(thread)}</p>
                      {thread.updated_at && (
                        <p className="thread-meta clamp-1">Last active {new Date(thread.updated_at).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                <div className="thread-actions">
                  <button type="button" className="button" onClick={() => navigate(`/chat/${thread.id}`)}>
                    Resume
                  </button>
                  <button
                    type="button"
                    className="button ghost"
                    disabled={renamingId === thread.id}
                    onClick={() => {
                      setRenamingTitle(thread.title ?? '')
                      setRenamingId(thread.id)
                    }}
                  >
                    Rename
                  </button>
                  <button type="button" className="button danger" onClick={() => handleDeleteThread(thread.id)}>
                    Delete
                  </button>
                </div>
                {renamingId === thread.id && (
                  <div className="thread-rename-row">
                    <input
                      type="text"
                      value={renamingTitle}
                      onChange={(event) => setRenamingTitle(event.target.value)}
                      placeholder="Enter a new title"
                    />
                    <button
                      type="button"
                      className="button primary"
                      disabled={!renamingTitle.trim()}
                      onClick={() => handleRenameThread(thread.id)}
                    >
                      Save
                    </button>
                    <button type="button" className="button ghost" onClick={() => setRenamingId(null)}>
                      Cancel
                    </button>
                  </div>
                )}
                </li>
              )
            })}
          </ul>
          {deleteError && <p className="error-state">{deleteError}</p>}
        </section>

        <section className="dashboard-card figure-selector-card">
          <div className="dashboard-card-heading">
            <h2>Start a new conversation</h2>
            <p className="hint">Search any figure or jump in with a featured pick.</p>
          </div>

          <div className="figure-selector-layout">
            <div className="typeahead-shell" ref={typeaheadRef}>
              <label htmlFor="figure-search">Find a figure</label>
              <div className="search-row">
                <input
                  id="figure-search"
                  type="search"
                  placeholder="Search by name, era, or title"
                  value={searchTerm}
                  onChange={(event) => handleSearchChange(event.target.value)}
                  onFocus={() => setTypeaheadOpen(true)}
                  onKeyDown={handleTypeaheadKeyDown}
                  disabled={!canRun}
                  aria-expanded={typeaheadOpen}
                  aria-controls="figure-typeahead"
                  ref={typeaheadInputRef}
                />
                <button
                  type="button"
                  className="button ghost"
                  onClick={() => {
                    if (searchTimer) {
                      window.clearTimeout(searchTimer)
                      setSearchTimer(null)
                    }
                    setSearchTerm('')
                    setSearchResults([])
                    setTypeaheadOpen(true)
                    setHighlightIndex(-1)
                  }}
                  disabled={!searchTerm}
                >
                  Clear
                </button>
              </div>
              {searchError && <p className="error-state">{searchError}</p>}
              {typeaheadOpen && (
                <div className="typeahead-panel" id="figure-typeahead">
                  {searchLoading && <p className="hint">Searching…</p>}
                  {!searchLoading && !visibleTypeahead.length && (
                    <p className="hint">
                      {searchTerm.trim().length < 2
                        ? 'Type at least two letters or pick a featured figure.'
                        : 'No matches yet—try a different spelling.'}
                    </p>
                  )}
                  <ul className="search-results typeahead-results">
                    {visibleTypeahead.map((figure, index) => {
                      const descriptor = describeFigure(figure)
                      const isActive = index === highlightIndex
                      const image = resolveFigureImage(figure)
                      const fallbackLabel = figure.name ?? figure.slug ?? 'PiT'
                      const thumbSize = searchTerm.trim().length >= 2 ? 52 : 56
                      return (
                        <li
                          key={figure.slug}
                          className={`search-result typeahead-option${isActive ? ' active' : ''}`}
                          onMouseEnter={() => setHighlightIndex(index)}
                          onMouseLeave={() => setHighlightIndex(-1)}
                          onMouseDown={(event) => {
                            event.preventDefault()
                            selectFigure(figure)
                          }}
                        >
                          <FigureThumb
                            src={image}
                            alt={figure.name ?? 'Figure portrait'}
                            size={thumbSize}
                            variant="circle"
                            fallbackText={fallbackLabel}
                          />
                          <div className="thread-body">
                            <p className="thread-title clamp-1">{figure.name}</p>
                            <p className="thread-subtitle clamp-2">{descriptor ?? figure.slug}</p>
                            <SourceDepthBadge depth={figure.source_depth} variant="compact" />
                          </div>
                          <div className="thread-actions">
                            <button
                              type="button"
                              className="button primary"
                              disabled={creatingThreadSlug === figure.slug}
                              onMouseDown={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                selectFigure(figure)
                              }}
                            >
                              {creatingThreadSlug === figure.slug ? 'Creating…' : 'Start chat'}
                            </button>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                  {!searchLoading && suggestionFigures.length > 0 && searchTerm.trim().length < 2 && (
                    <div className="hint">Recently active figures</div>
                  )}
                </div>
              )}
            </div>

            <div className="featured-figures">
              <div className="featured-heading">
                <p className="eyebrow strong">Featured figures</p>
                <p className="hint">Jump back into popular picks.</p>
              </div>
              <div className="featured-grid">
                {featuredFigures.map((figure) => {
                  const descriptor = describeFigure(figure)
                  const image = resolveFigureImage(figure)
                  const fallbackLabel = figure.name ?? figure.slug ?? 'PiT'
                  return (
                    <button
                      key={figure.slug}
                      type="button"
                      className="featured-card"
                      onClick={() => selectFigure(figure)}
                      disabled={creatingThreadSlug === figure.slug}
                    >
                      <FigureThumb
                        src={image}
                        alt={figure.name ?? 'Figure portrait'}
                        size={64}
                        variant="circle"
                        fallbackText={fallbackLabel}
                      />
                      <div className="featured-copy">
                        <p className="thread-title clamp-1">{figure.name}</p>
                        <p className="thread-subtitle clamp-2">{descriptor ?? figure.slug}</p>
                        <SourceDepthBadge depth={figure.source_depth} variant="compact" />
                      </div>
                      <span className="featured-cta">Start</span>
                    </button>
                  )
                })}
                {!featuredFigures.length && <p className="hint">No featured figures yet. Start a search to begin.</p>}
              </div>
            </div>
          </div>

          {createError && (
            <div className="inline-error-card">
              <div>
                <p className="error-title">Could not start chat</p>
                <p className="error-state">{createError}</p>
              </div>
              {lastCreateSlug && (
                <button
                  type="button"
                  className="button"
                  disabled={Boolean(creatingThreadSlug)}
                  onClick={() => handleStartChat(lastCreateSlug)}
                >
                  Retry
                </button>
              )}
            </div>
          )}
        </section>

        <section className="dashboard-card dashboard-defaults-card">
          <div className="dashboard-card-heading">
            <h2>Account defaults</h2>
            {settingsLoading && <span className="badge">Loading…</span>}
          </div>
          <p className="settings-hint">These defaults apply when you start a chat. Adjust per thread inside the chat controls.</p>
          {settingsError && <p className="error-state">{settingsError}</p>}
          {!settingsError && (
            <div className="defaults-grid" aria-label="Current account defaults">
              <div className="defaults-row">
                <p className="defaults-label">Language style</p>
                <p className="defaults-value">{getLanguageModeLabel(effectiveSettings.language_mode)}</p>
              </div>
              <div className="defaults-row">
                <p className="defaults-label">Knowledge scope</p>
                <p className="defaults-value">{getKnowledgeScopeLabel(effectiveSettings.knowledge_scope)}</p>
              </div>
              <div className="defaults-row">
                <p className="defaults-label">Tone</p>
                <p className="defaults-value">{getToneLevelLabel(effectiveSettings.tone_level)}</p>
              </div>
            </div>
          )}
          <div className="defaults-actions">
            <button type="button" className="button" onClick={() => navigate('/settings')}>
              Edit defaults
            </button>
          </div>
        </section>
      </div>
    </section>
  )
}

export default Dashboard
